/**
 * 슬라이드 블록 렌더 컴포넌트 모음. 모든 사이즈는 절대 픽셀 (1920×1080 컨텍스트).
 * 디자인 토큰은 부모 SlideCanvas 가 노출한 CSS 변수로.
 */
import type { CSSProperties } from 'react';

type Align = 'left' | 'center' | 'right';

export function Heading({
  level,
  text,
  align = 'left',
}: {
  level?: 'hero' | 'section' | 'sub';
  text: string;
  align?: Align;
}) {
  const lv = level ?? 'section';
  const fontSize =
    lv === 'hero'
      ? 'var(--osd-size-hero)'
      : lv === 'section'
        ? 'calc(var(--osd-size-hero) * 0.55)'
        : 'calc(var(--osd-size-hero) * 0.35)';
  return (
    <h2
      style={{
        margin: 0,
        fontFamily: 'var(--osd-font-display)',
        fontSize,
        lineHeight: 1.1,
        wordBreak: 'keep-all',
        color: 'var(--osd-text)',
        textAlign: align,
      }}
    >
      {text}
    </h2>
  );
}

export function Body({
  text,
  size = 'md',
  align = 'left',
}: {
  text: string;
  size?: 'lg' | 'md' | 'sm';
  align?: Align;
}) {
  const fontSize =
    size === 'lg'
      ? 'calc(var(--osd-size-body) * 1.3)'
      : size === 'sm'
        ? 'calc(var(--osd-size-body) * 0.8)'
        : 'var(--osd-size-body)';
  return (
    <p
      style={{
        margin: 0,
        fontSize,
        lineHeight: 1.45,
        wordBreak: 'keep-all',
        color: 'var(--osd-text)',
        textAlign: align,
        whiteSpace: 'pre-wrap',
      }}
    >
      {text}
    </p>
  );
}

export function Bullets({
  items,
  style = 'dot',
}: {
  items: string[];
  style?: 'dot' | 'dash' | 'numbered';
}) {
  const Tag = style === 'numbered' ? 'ol' : 'ul';
  const liStyle: CSSProperties = {
    fontSize: 'var(--osd-size-body)',
    lineHeight: 1.45,
    wordBreak: 'keep-all',
    color: 'var(--osd-text)',
    marginBottom: 24,
  };
  return (
    <Tag
      style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
      }}
    >
      {items.map((it, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: bullets items 는 안정 id 없음 — 같은 페이지 안에서만 렌더되므로 index 키로 충분.
        <li key={i} style={liStyle}>
          <span
            style={{
              display: 'inline-block',
              width: 36,
              color: 'var(--osd-accent)',
              fontWeight: 700,
            }}
          >
            {style === 'numbered' ? `${i + 1}.` : style === 'dash' ? '—' : '•'}
          </span>
          {it}
        </li>
      ))}
    </Tag>
  );
}

export function Quote({ text, attribution }: { text: string; attribution?: string }) {
  return (
    <figure style={{ margin: 0 }}>
      <blockquote
        style={{
          margin: 0,
          fontFamily: 'var(--osd-font-display)',
          fontSize: 'calc(var(--osd-size-hero) * 0.4)',
          lineHeight: 1.3,
          wordBreak: 'keep-all',
          color: 'var(--osd-text)',
          fontStyle: 'italic',
          textAlign: 'center',
        }}
      >
        “{text}”
      </blockquote>
      {attribution && (
        <figcaption
          style={{
            marginTop: 32,
            fontSize: 'calc(var(--osd-size-body) * 0.85)',
            color: 'var(--osd-accent)',
            textAlign: 'center',
            wordBreak: 'keep-all',
          }}
        >
          — {attribution}
        </figcaption>
      )}
    </figure>
  );
}

export function Image({
  src,
  fit = 'cover',
  caption,
}: {
  src?: string;
  fit?: 'cover' | 'contain';
  caption?: string;
  assetId?: string;
}) {
  return (
    <figure
      style={{
        margin: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: 'var(--osd-radius)',
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {src ? (
          <img
            src={src}
            alt={caption ?? ''}
            style={{
              width: '100%',
              height: '100%',
              objectFit: fit === 'contain' ? 'contain' : 'cover',
            }}
          />
        ) : (
          <span style={{ color: 'var(--osd-text)', opacity: 0.5, fontSize: 24 }}>
            (이미지 자산 없음)
          </span>
        )}
      </div>
      {caption && (
        <figcaption
          style={{
            fontSize: 'calc(var(--osd-size-body) * 0.7)',
            color: 'var(--osd-text)',
            opacity: 0.7,
            wordBreak: 'keep-all',
          }}
        >
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

export function Kpi({
  label,
  value,
  delta,
}: {
  label: string;
  value: string;
  delta?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        flex: 1,
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontSize: 'calc(var(--osd-size-body) * 0.9)',
          color: 'var(--osd-text)',
          opacity: 0.7,
          wordBreak: 'keep-all',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: 'var(--osd-font-display)',
          fontSize: 'calc(var(--osd-size-hero) * 0.6)',
          lineHeight: 1.0,
          color: 'var(--osd-accent)',
          wordBreak: 'keep-all',
        }}
      >
        {value}
      </span>
      {delta && (
        <span
          style={{
            fontSize: 'calc(var(--osd-size-body) * 0.85)',
            color: 'var(--osd-text)',
            opacity: 0.8,
          }}
        >
          {delta}
        </span>
      )}
    </div>
  );
}

export function Spacer({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const h = size === 'lg' ? 80 : size === 'sm' ? 24 : 48;
  return <div style={{ height: h }} aria-hidden />;
}
